import { useState, useEffect, useCallback, type MouseEvent } from 'react';
import {
  Avatar,
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  makeStyles,
  Select,
  FormControl,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import Settings from '@material-ui/icons/Settings';
import ExitToApp from '@material-ui/icons/ExitToApp';
import Palette from '@material-ui/icons/Palette';
import Language from '@material-ui/icons/Language';
import { useApi, identityApiRef, appThemeApiRef } from '@backstage/core-plugin-api';
import { appLanguageApiRef, useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { useSidebarOpenState } from '@backstage/core-components';
import { useNavigate } from 'react-router-dom';
import { userMenuTranslationRef } from '../../translations/features/userMenu/userMenu';
import { setStoredLanguage } from '../../utils/languageStorage';
import { getStoredTheme, setStoredTheme, clearStoredTheme } from '../../utils/themeStorage';

const useStyles = makeStyles(theme => ({
  userButton: {
    width: '100%',
    padding: theme.spacing(1, 2.5),
    borderRadius: theme.shape.borderRadius,
    textTransform: 'none',
    justifyContent: 'flex-start',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    minWidth: 0,
  },
  avatar: {
    width: 32,
    height: 32,
    fontSize: '0.875rem',
    backgroundColor: theme.palette.primary.main,
    flexShrink: 0,
  },
  userDetails: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    minWidth: 0,
    flex: 1,
    width: '100%',
    maxWidth: '100%',
  },
  userName: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    maxWidth: '100%',
  },
  userEmail: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    maxWidth: '100%',
  },
  userNameSidebar: {
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.common.white,
    width: '100%',
    maxWidth: '100%',
    textAlign: 'left',
  },
  userEmailSidebar: {
    fontSize: '0.75rem',
    color: alpha(theme.palette.common.white, 0.7),
    lineHeight: 1.2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    maxWidth: '100%',
    textAlign: 'left',
  },
  menuPaper: {
    minWidth: 340,
    marginTop: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[8],
  },
  menuHeader: {
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  menuItem: {
    padding: theme.spacing(1, 1.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  menuItemIcon: {
    minWidth: 40,
    color: theme.palette.text.secondary,
  },
  checkIcon: {
    marginLeft: 'auto',
    color: theme.palette.primary.main,
  },
  divider: {
    margin: theme.spacing(0.5, 0),
  },
  themeSelect: {
    marginLeft: '32px',
    minWidth: 120,
    '& .MuiSelect-root': {
      padding: theme.spacing(1, 1),
      fontSize: '0.75rem',
    },
  },
}));

interface UserInfo {
  name: string;
  email: string;
  avatar?: string;
}

export const UserMenuDropdown = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslationRef(userMenuTranslationRef);

  const identityApi = useApi(identityApiRef);
  const appLanguageApi = useApi(appLanguageApiRef);
  const appThemeApi = useApi(appThemeApiRef);
  const { isOpen } = useSidebarOpenState();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [currentLanguage, setCurrentLanguage] = useState<string>('pt-BR');
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(null);

  const availableThemes = appThemeApi.getInstalledThemes();

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const identity = await identityApi.getBackstageIdentity();
        const profile = await identityApi.getProfileInfo();
        
        setUserInfo({
          name: profile.displayName || identity.userEntityRef.split('/')[1] || 'Usuário',
          email: profile.email || '',
          avatar: profile.picture,
        });
      } catch {
        setUserInfo({
          name: 'Usuário',
          email: '',
        });
      }
    };

    loadUserInfo();
  }, [identityApi]);

  useEffect(() => {
    const initialLanguage = appLanguageApi.getLanguage().language;
    setCurrentLanguage(initialLanguage);

    const subscription = appLanguageApi.language$().subscribe(({ language }) => {
      setCurrentLanguage(language);
    });

    return () => subscription.unsubscribe();
  }, [appLanguageApi]);

  useEffect(() => {
    const initialTheme = getStoredTheme();
    setSelectedThemeId(initialTheme); // null = Automático

    const subscription = appThemeApi.activeThemeId$().subscribe((themeId) => {
      const storedTheme = getStoredTheme();

      if (!storedTheme) {
        setSelectedThemeId(null);
      } else if (themeId && themeId !== selectedThemeId) {
        setSelectedThemeId(themeId);
      }
    });

    return () => subscription.unsubscribe();
  }, [appThemeApi, selectedThemeId]);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = useCallback((lang: string) => {
    appLanguageApi.setLanguage(lang);
    setStoredLanguage(lang as 'en' | 'pt-BR');
    setCurrentLanguage(lang);
  }, [appLanguageApi]);

  const handleThemeChange = useCallback((themeId: string) => {
    if (themeId === 'auto') {
      clearStoredTheme();
      setSelectedThemeId(null);
      appThemeApi.setActiveThemeId(undefined);
    } else {
      setStoredTheme(themeId);
      appThemeApi.setActiveThemeId(themeId);
      setSelectedThemeId(themeId);
    }
  }, [appThemeApi]);

  const handleLogout = useCallback(async () => {
    try {
      await identityApi.signOut();
      window.location.href = '/';
    } catch {
      // Silently handle logout errors
    }
  }, [identityApi]);

  const getInitials = (name: string): string => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const availableLanguages = appLanguageApi.getAvailableLanguages().languages;

  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        className={classes.userButton}
        onClick={handleClick}
        aria-label={t('button.ariaLabel')}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
      >
        <Box className={classes.userInfo}>
          <Avatar
            src={userInfo?.avatar}
            className={classes.avatar}
            alt={userInfo?.name || 'Usuário'}
          >
            {userInfo?.name ? getInitials(userInfo.name) : 'U'}
          </Avatar>
          {isOpen && (
            <Box className={classes.userDetails}>
              <Typography className={classes.userNameSidebar}>
                {userInfo?.name || 'Usuário'}
              </Typography>
              {userInfo?.email && (
                <Typography className={classes.userEmailSidebar}>
                  {userInfo.email}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          className: classes.menuPaper,
        }}
        getContentAnchorEl={null}
      >
        <Box className={classes.menuHeader}>
          <Box className={classes.userInfo}>
            <Avatar
              src={userInfo?.avatar}
              className={classes.avatar}
              alt={userInfo?.name || 'Usuário'}
            >
              {userInfo?.name ? getInitials(userInfo.name) : 'U'}
            </Avatar>
            <Box className={classes.userDetails}>
              <Typography className={classes.userName}>
                {userInfo?.name || 'Usuário'}
              </Typography>
              {userInfo?.email && (
                <Typography className={classes.userEmail}>
                  {userInfo.email}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Divider className={classes.divider} />

        <MenuItem
          className={classes.menuItem}
          onClick={(e) => e.stopPropagation()}
        >
          <ListItemIcon className={classes.menuItemIcon}>
            <Palette fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('menu.theme')} />
          <FormControl className={classes.themeSelect} size="small">
            <Select
              value={selectedThemeId || 'auto'}
              onChange={(e) => handleThemeChange(e.target.value as string)}
              variant="outlined"
            >
              <MenuItem key="auto" value="auto">
                {t('menu.theme.auto')}
              </MenuItem>
              {availableThemes.map((theme) => (
                <MenuItem key={theme.id} value={theme.id}>
                  {theme.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </MenuItem>

        {availableLanguages.length > 1 && [
          <MenuItem
            key="language-menu-item"
            className={classes.menuItem}
            onClick={() => {
              const nextLang = currentLanguage === 'pt-BR' ? 'en' : 'pt-BR';
              handleLanguageChange(nextLang);
            }}
          >
            <ListItemIcon className={classes.menuItemIcon}>
              <Language fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('menu.language')} />
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 'auto' }}>
              {currentLanguage === 'pt-BR' ? 'PT-BR' : 'EN'}
            </Typography>
          </MenuItem>,
          <Divider key="language-divider" className={classes.divider} />
        ]}

        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            navigate('/settings');
            handleClose();
          }}
        >
          <ListItemIcon className={classes.menuItemIcon}>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('menu.settings')} />
        </MenuItem>

        <Divider className={classes.divider} />

        <MenuItem
          className={classes.menuItem}
          onClick={handleLogout}
        >
          <ListItemIcon className={classes.menuItemIcon}>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={t('menu.logout')} />
        </MenuItem>
      </Menu>
    </>
  );
};
